import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { notificationsApi } from "@/api/endpoints/notifications";
import { NOTIFICATION_KEYS } from "./use-notifications";
import { playNotificationSound, unlockNotificationAudio } from "../lib/notification-sound";
import type { NotificationItem } from "../types";

const RECONNECT_DELAY_MS = 3_000;
// When the tab regains focus, the auth provider re-validates the session with a
// 5s timeout. Reconnecting the stream immediately would compete with that (dev
// proxy has a small connection pool), risking a session timeout → logout. Wait
// a beat so the auth check wins the race.
const REFOCUS_RECONNECT_DELAY_MS = 2_500;

function isSelfEvident(n: NotificationItem): boolean {
  if (n.type !== "chat_complete") return false;
  if (typeof document === "undefined") return false;
  const path = window.location.pathname;
  const onChat = path === "/" || path.startsWith("/chat");
  return document.visibilityState === "visible" && onChat;
}

/**
 * Subscribe to the user's notification SSE stream while the tab is visible. On
 * each pushed notification we refresh the bell and (unless self-evident) play
 * the ding + toast.
 *
 * The stream is a long-lived connection, so we deliberately DROP it when the tab
 * is hidden and reopen it (after a short delay) when it becomes visible again.
 * That frees the connection while the user is away and, critically, keeps the
 * reconnect from starving the on-focus session re-validation (which would log
 * the user out on timeout). No-ops while unauthenticated and in USE_MOCK mode.
 */
export function useNotificationsStream(userId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;
    unlockNotificationAudio();

    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const clearTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const stop = () => {
      clearTimer();
      if (controller) {
        controller.abort();
        controller = null;
      }
    };

    const connect = () => {
      clearTimer();
      if (cancelled || controller) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const mine = new AbortController();
      controller = mine;
      notificationsApi
        .streamNotifications((n) => {
          queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
          if (isSelfEvident(n)) {
            notificationsApi
              .markRead(n.id)
              .then(() => queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }))
              .catch(() => {});
            return;
          }
          playNotificationSound();
          toast(n.title, { description: n.body ?? undefined });
        }, mine.signal)
        .catch(() => {
          /* network drop / abort — reconnect below */
        })
        .finally(() => {
          if (controller === mine) controller = null;
          if (cancelled) return;
          if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Delay so the on-focus session re-validation isn't starved.
        clearTimer();
        reconnectTimer = setTimeout(connect, REFOCUS_RECONNECT_DELAY_MS);
      } else {
        stop(); // free the connection while hidden
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      connect();
    }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [userId, queryClient]);
}
